import { useEffect, useState } from 'react'
import {
  Grid,
  Paper,
  TableContainer,
  TableBody,
  Table,
  TableHead,
  TableRow,
  TableCell,
  useTheme,
  Typography,
  TablePagination,
  MenuItem,
  Menu,
  IconButton,
  LinearProgress,
  InputAdornment,
  TextField,
  Snackbar,
  Alert,
  Button,
} from '@mui/material'
import { client, refreshToken } from '../../utils/client'
import Bugsnag from '@bugsnag/js'
import { useNavigate } from 'react-router-dom'
import { MdMoreVert, MdCheck, MdFilterAlt, MdSearch } from 'react-icons/md'
import { ExternalInscriptionListRespType, AlertType } from '../../utils/sharedTypes'
import { useParams } from 'react-router-dom'
import { secondsToHMS } from '../../utils/misc'
import StatusLabel from './StatusLabel'

type FilterType = {
  status: null | string
  global_search: '' | string
}

type InscriptionTablePropsType = {
  refetch: boolean
}

const InscriptionTable = ({ refetch }: InscriptionTablePropsType) => {
  const navigate = useNavigate()
  const { provider_id } = useParams()
  const theme = useTheme()
  const [isLoading, setIsLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState<number>(0)
  const [state, setState] = useState<ExternalInscriptionListRespType>({
    count: 0,
    next: '',
    previous: '',
    results: [],
  })
  const [alert, setAlert] = useState<AlertType>({
    severity: undefined,
    message: '',
  })

  const [url, setUrl] = useState('')
  const [anchorValidated, setAnchorValidated] = useState<null | HTMLElement>(null)
  const [filters, setFilters] = useState<FilterType>({
    status: null,
    global_search: '',
  })

  useEffect(() => {
    if (provider_id) {
      let url = `/registration/external/${provider_id}/?page=${currentPage + 1}`
      if (filters.status !== null) {
        url = url + `&status=${filters.status.toString()}`
      }
      if (filters.global_search !== '') {
        url = url + `&global_search=${filters.global_search.toString()}`
      }
      setUrl(url)
    }
  }, [currentPage, filters, provider_id])

  const getRegistrations = () => {
    client
      .get(url)
      .then((resp) => {
        const data = resp.data as ExternalInscriptionListRespType
        setState(data)
      })
      .catch((err: Error) => {
        Bugsnag.notify(err)
        console.error('err', err)
        refreshToken(getRegistrations)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  useEffect(() => {
    if (!isLoading && url) {
      setIsLoading(true)
      getRegistrations()
    }
    return () => {
      setIsLoading(false)
    }
  }, [url, refetch])

  const discard = (global_id: string) => {
    if (provider_id) {
      client
        .patch(`/registration/external/${provider_id}/${global_id}/discard/`)
        .then((resp) => {
          if (resp.status === 200) {
            setAlert({ severity: 'info', message: 'Inscription supprimée' })
            getRegistrations()
          } else {
            setAlert({ severity: 'error', message: 'Erreur lors du refus' })
          }
        })
        .catch((err: Error) => {
          setAlert({ severity: 'error', message: 'Erreur lors du refus' })
          Bugsnag.notify(err)
          console.error(err)
        })
    }
  }

  return (
    <Paper>
      <Grid container alignItems="center" sx={{ p: 1 }}>
        <Grid item sx={{ width: '100%' }}>
          <TextField
            placeholder="Recherche"
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MdSearch size="20" />
                </InputAdornment>
              ),
            }}
            value={filters.global_search}
            onChange={(e: React.FocusEvent<HTMLInputElement>) => {
              setFilters((prevState) => {
                return {
                  ...prevState,
                  global_search: e?.target.value,
                }
              })
            }}
            sx={{
              width: '200px',
              input: {
                fontSize: 16,
              },
              '&:focus-within': {
                width: '100%',
              },
              transition: 'width 0.25s',
            }}
          />
        </Grid>
      </Grid>
      {isLoading ? <LinearProgress /> : <div style={{ height: '4px' }}></div>}
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="left">Prénom</TableCell>
              <TableCell align="left">Nom</TableCell>
              <TableCell align="left">Produit</TableCell>
              <TableCell align="left">Financement</TableCell>
              <TableCell align="left">Email</TableCell>
              <TableCell align="left">Téléphone</TableCell>
              <TableCell align="left">Temps de connexion</TableCell>
              <TableCell align="left">Taux de réalisation</TableCell>
              <TableCell align="center">
                <Grid sx={{ display: 'flex', justifyContent: 'center' }}>
                  Statut
                  <IconButton
                    sx={{ p: '4px' }}
                    onClick={(e) => {
                      setAnchorValidated(e.currentTarget)
                    }}
                  >
                    {filters.status !== null ? (
                      <MdFilterAlt size="20" style={{ color: theme.palette.primary.main }} />
                    ) : (
                      <MdMoreVert size="20" style={{ color: theme.palette.grey[500] }} />
                    )}
                  </IconButton>
                </Grid>
              </TableCell>
              <TableCell align="center">Supprimer</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {state.results.length > 0 ? (
              state.results.map((result) => (
                <TableRow
                  onClick={() => {
                    if (provider_id) {
                      navigate(`/providers/${provider_id}/registration/${result.global_id}/`)
                    }
                  }}
                  key={result.global_id}
                  sx={{
                    '&:hover': {
                      cursor: 'pointer',
                      backgroundColor: theme.palette.grey[200],
                    },
                  }}
                >
                  <TableCell align="left">{result.first_name || '-'}</TableCell>
                  <TableCell align="left">{result.last_name || '-'}</TableCell>
                  <TableCell align="left">{result.product?.name || '-'}</TableCell>
                  <TableCell align="left">{result.funding_organism?.code || '-'}</TableCell>
                  <TableCell align="left">{result.email || '-'}</TableCell>
                  <TableCell align="left">{result.mobile_phone || '-'}</TableCell>
                  <TableCell align="left">
                    {result.sf_case ? secondsToHMS(result.sf_case?.walter_session_time || 0, false) : '-'}
                  </TableCell>
                  <TableCell align="left">
                    {result.sf_case
                      ? result.sf_case.realized_rate
                        ? `${Math.round(result.sf_case.realized_rate * 100)}%`
                        : '0%'
                      : '-'}
                  </TableCell>
                  <TableCell align="center">
                    <StatusLabel registration={result} />
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      disabled={result.status !== 'Submitted'}
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        if (result.global_id) {
                          e.preventDefault()
                          e.stopPropagation()
                          discard(result.global_id)
                        }
                      }}
                    >
                      Supprimer
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={13} align="center" style={{ height: '250px', color: theme.palette.text.disabled }}>
                  <Grid container direction="column" justifyContent="center">
                    <Grid item>
                      {!isLoading && (
                        <Typography variant="h6" sx={{ fontStyle: 'italic' }}>
                          Aucune inscription
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[state.results.length]}
          component="div"
          count={state.count}
          rowsPerPage={state.results.length}
          page={currentPage}
          onPageChange={(_event, newPage) => {
            window.scrollTo(0, 0)
            setCurrentPage(newPage)
          }}
        />
      </TableContainer>

      <Menu
        anchorEl={anchorValidated}
        open={Boolean(anchorValidated)}
        onClose={() => {
          setAnchorValidated(null)
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem
          onClick={() => {
            setFilters((prevState) => {
              return {
                ...prevState,
                status: 'Submitted',
              }
            })
            setAnchorValidated(null)
          }}
        >
          <MdCheck style={{ marginRight: 5, visibility: filters.status === 'Submitted' ? 'visible' : 'hidden' }} /> En
          attente
        </MenuItem>
        <MenuItem
          onClick={() => {
            setFilters((prevState) => {
              return {
                ...prevState,
                status: 'Validated',
              }
            })
            setAnchorValidated(null)
          }}
        >
          <MdCheck style={{ marginRight: 5, visibility: filters.status === 'Validated' ? 'visible' : 'hidden' }} />{' '}
          Validée
        </MenuItem>
        <MenuItem
          onClick={() => {
            setFilters((prevState) => {
              return {
                ...prevState,
                status: null,
              }
            })
            setAnchorValidated(null)
          }}
        >
          <MdCheck style={{ marginRight: 5, visibility: filters.status === null ? 'visible' : 'hidden' }} /> Tout
        </MenuItem>
      </Menu>
      <Snackbar
        open={Boolean(alert.message)}
        autoHideDuration={6000}
        onClose={() => {
          setAlert({ severity: undefined, message: '' })
        }}
      >
        <Alert
          onClose={() => {
            setAlert({ severity: undefined, message: '' })
          }}
          severity={alert.severity || 'info'}
          sx={{ width: '100%' }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </Paper>
  )
}

export default InscriptionTable
